import React, { useEffect, useState } from 'react';
import AgyleDictionaryField from '../../../../../../components/ui/selector/agyleDictionaryField';
import { useDebounce } from 'use-debounce';
import _, { isEmpty } from 'lodash';
import { directoryOfHealthcareApi } from '../../../../../../app/apiPrescription';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';

const getOrganizationLabel = (option) => option?.display || option?.name || '';

const OrganizationAutocomplete = ({ value, handleChangeValue, disabled, onClose }) => {
    const { t } = useTranslation();
    const controller = new AbortController();

    // Input states
    const [autocompleteOpen, setAutocompleteOpen] = useState(false);
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);
    const [autocompleteInputValue, setAutocompleteInputValue] = useState('');
    const [autocompleteLoading, setAutocompleteLoading] = useState(false);

    // debounced option
    const [autocompleteInputValueDebounced] = useDebounce(autocompleteInputValue, 250);

    // Value
    const [inputValue, setInputValue] = useState(null);

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    // Search
    useEffect(() => {
        if (autocompleteInputValueDebounced && !isEmpty(autocompleteInputValueDebounced) && autocompleteInputValueDebounced?.length > 2) {
            setAutocompleteLoading(true);
            setAutocompleteOpen(true);

            directoryOfHealthcareApi
                .searchOrganization({ searchTerm: autocompleteInputValueDebounced, signal: controller.signal })
                .then((result) => {
                    if (controller.signal.aborted) return;

                    if (result?.results) {
                        setAutocompleteOptions(result.results);
                    } else {
                        setAutocompleteOptions([]);
                    }
                    setAutocompleteLoading(false);
                })
                .catch(function (error) {
                    console.error('Autocomplete dictionaries error', error);
                    if (!controller.signal.aborted) {
                        setAutocompleteOptions([]);
                        setAutocompleteLoading(false);
                    }
                });
        } else {
            // Resetuj opcje gdy input jest za krótki
            setAutocompleteOptions([]);
            setAutocompleteLoading(false);
        }

        return () => {
            controller.abort();
        };
    }, [autocompleteInputValueDebounced]);

    // Prepopulate value
    useEffect(() => {
        setInputValue(value ?? null);
    }, [value]);

    const handleAutocompleteState = (val) => {
        setAutocompleteOpen(val);
    };

    const handleChange = (_, newValues) => {
        // Prevent changes if form is disabled
        if (disabled) return;

        handleChangeValue(newValues);
        setAutocompleteOpen(false);
    };

    const renderEndAdornment = () => {
        if (onClose) {
            return (
                <Box
                    sx={{
                        pr: 3,
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <IconButton
                        size="small"
                        fontSize="small"
                        onClick={() => {
                            if (onClose) {
                                onClose();
                            }
                        }}
                    >
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        return null;
    };

    return (
        <div style={{ width: '100%' }}>
            <AgyleDictionaryField
                size="small"
                noBorder={false}
                withNewBackground
                open={autocompleteOpen}
                onOpen={handleAutocompleteState.bind(this, true)}
                onClose={handleAutocompleteState.bind(this, false)}
                disabled={disabled || !isEmpty(value)}
                isOptionEqualToValue={(option, value) => option?.code === value?.code}
                getOptionLabel={(option) => getOrganizationLabel(option)}
                options={autocompleteOptions}
                loading={autocompleteLoading}
                filterOptions={(x) => x}
                noOptionsText={!isEmpty(autocompleteInputValueDebounced) ? t('No organizations found') : t('Type at least 3 characters')}
                loadingText={t('Loading organizations...')}
                onInputChange={(event, newInputValue) => setAutocompleteInputValue(newInputValue)}
                value={value}
                onChange={handleChange}
                disableClearable
                placeholder={t('Search for organization or pharmacy...')}
                startIcon={
                    <Box
                        sx={{
                            pl: 1,
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        <SearchIcon />
                    </Box>
                }
                endAdornmentAddons={renderEndAdornment}
                renderOption={(props, option) => (
                    <Box
                        component="li"
                        {...props}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            py: 1,
                        }}
                    >
                        <Box
                            sx={{
                                color: 'text.secondary',
                                display: 'flex',
                                mt: 0.25,
                            }}
                        >
                            <LocalPharmacyIcon fontSize="small" />
                        </Box>

                        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {getOrganizationLabel(option)}
                                </Typography>

                                {option?.type && (
                                    <Box
                                        component="span"
                                        sx={{
                                            px: 0.75,
                                            py: 0.125,
                                            borderRadius: 1,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            lineHeight: 1.5,
                                            color: 'primary.main',
                                            bgcolor: 'primary.50',
                                        }}
                                    >
                                        {option.type}
                                    </Box>
                                )}
                            </Box>

                            {option?.address && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        lineHeight: 1.35,
                                        display: 'block',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {option.address}
                                </Typography>
                            )}

                            {option?.telecom && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Tel: {option.telecom}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            />
        </div>
    );
};

export default OrganizationAutocomplete;
